// src/lib/support_context.ts — contextul TEHNIC al paginii pentru tichetele de suport (F14-T2). PUR.
// Lista albă e în lib/support (SUPPORT_CONTEXT); aici e CONSTRUIREA ei din ce strânge browserul:
// buffer-ele circulare cu ultimele erori de client și cereri API eșuate, curățarea (fără emailuri —
// un mesaj de eroare poate cita o adresă din formular; fără query string — căutările poartă textul
// omului), plafoanele și căderea celor mai vechi note când depășim bugetul de octeți.

import { MAX_SUPPORT_CONTEXT_BYTES, normalize_context, type SupportContext } from "./support"

export const MAX_NOTES = 10
export interface ClientErrorNote {
	message: string
	source?: string
	ts: string
}
export interface FailedApiNote {
	method: string
	path: string
	status: number
	ts: string
}

/** Buffer circular: păstrează ultimele `max` note (cele mai vechi cad). */
export function push_note<T>(buf: T[], note: T, max = MAX_NOTES): void {
	buf.push(note)
	while (buf.length > max) {
		buf.shift()
	}
}

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g
/** Fără adrese de email, fără spații multiple, tăiat la `max`. */
export function scrub(s: unknown, max: number): string {
	return String(s ?? "")
		.replace(EMAIL_RE, "[email]")
		.replace(/\s+/g, " ")
		.trim()
		.slice(0, max)
}
const no_query = (s: string) => s.split("?")[0]
const cut = (s: string | undefined, max: number) => (s ? s.slice(0, max) : "")

export interface ContextParts {
	route: string
	map?: string
	layer?: string
	feature?: string
	role?: string
	org_slug?: string
	features_on?: readonly string[]
	features_off?: readonly string[]
	app_version?: string
	ua?: string
	viewport?: string
	online?: boolean
	standalone?: boolean
	errors?: readonly ClientErrorNote[]
	failed_api?: readonly FailedApiNote[]
	now?: Date
}

/** Construiește contextul VALID (lista albă + plafoane) din părțile strânse de browser. */
export function build_context(p: ContextParts): SupportContext {
	const now = (p.now ?? new Date()).toISOString()
	let errors = (p.errors ?? []).slice(-MAX_NOTES).map((e) => ({
		message: scrub(e.message, 300) || "eroare",
		...(e.source ? { source: scrub(no_query(e.source), 200) } : {}),
		ts: cut(e.ts, 40),
	}))
	let failed = (p.failed_api ?? []).slice(-MAX_NOTES).map((f) => ({
		method: cut(f.method, 8).toUpperCase() || "GET",
		path: scrub(no_query(f.path), 200),
		status: Math.min(999, Math.max(0, Math.trunc(Number.isFinite(f.status) ? f.status : 0))),
		ts: cut(f.ts, 40),
	}))
	const base: SupportContext = {
		route: cut(no_query(p.route), 200) || "/",
		...(p.map ? { map: cut(p.map, 120) } : {}),
		...(p.layer ? { layer: cut(p.layer, 120) } : {}),
		...(p.feature ? { feature: cut(p.feature, 64) } : {}),
		...(p.role ? { role: cut(p.role, 32) } : {}),
		...(p.org_slug ? { org_slug: cut(p.org_slug, 120) } : {}),
		...(p.features_on?.length
			? { features_on: p.features_on.slice(0, 16).map((x) => cut(x, 40)) }
			: {}),
		...(p.features_off?.length
			? { features_off: p.features_off.slice(0, 16).map((x) => cut(x, 40)) }
			: {}),
		...(p.app_version ? { app_version: cut(p.app_version, 40) } : {}),
		...(p.ua ? { ua: scrub(p.ua, 300) } : {}),
		...(p.viewport ? { viewport: cut(p.viewport, 20) } : {}),
		...(p.online !== undefined ? { online: p.online } : {}),
		...(p.standalone !== undefined ? { standalone: p.standalone } : {}),
		captured_at: now,
	}
	const assemble = (): SupportContext => ({
		...base,
		...(errors.length ? { errors } : {}),
		...(failed.length ? { failed_api: failed } : {}),
	})
	let ctx = assemble()
	// bugetul de octeți: cad întâi notele cele mai vechi (erorile, apoi cererile eșuate)
	while (
		JSON.stringify(ctx).length > MAX_SUPPORT_CONTEXT_BYTES &&
		(errors.length || failed.length)
	) {
		if (errors.length >= failed.length) {
			errors = errors.slice(1)
		} else {
			failed = failed.slice(1)
		}
		ctx = assemble()
	}
	return normalize_context(ctx)
}
